import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthField } from "@/components/auth/AuthLayout";
import { Pick, REGIONS } from "@/components/event-setup/BasicsSection";

export const TITLES = ["Prof", "Dr.", "MLS", "Rev.", "Mr.", "Ms.", "Miss", "Madam"];
const OTHER = "Other";

export const fieldTestId = (f) => (f.builtin ? `profile-${f.key.replace(/_/g, "-")}-input` : `profile-custom-${f.key}`);

const Title = ({ f, value, onChange, label }) => {
  const isOther = !TITLES.includes(value);
  return (
    <>
      <AuthField label={label}><Pick value={isOther ? OTHER : value} onChange={(v) => onChange(v === OTHER ? "" : v)} options={[...TITLES, OTHER]} testId="profile-title-select" className="h-11" /></AuthField>
      {isOther && <AuthField label={`Specify title${f.required ? " *" : ""}`}><Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. Mrs., Hon., Ing." className="h-11" data-testid="profile-title-other-input" /></AuthField>}
    </>
  );
};

const Choice = ({ value, onChange, options, testId }) => (
  <div className="flex gap-2" role="radiogroup">
    {options.map((s) => (
      <button key={s} type="button" role="radio" aria-checked={value === s} onClick={() => onChange(s)} data-testid={`${testId}-${s.toLowerCase()}`}
        className={`flex-1 h-11 rounded-md border text-sm font-semibold transition-colors ${value === s ? "border-[#E60000] bg-[#FFF0F0] text-[#E60000]" : "border-[#E5E7EB] hover:border-gray-400"}`}>{s}</button>
    ))}
  </div>
);

const Multi = ({ value = [], onChange, options, testId }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testId}>
    {options.map((o, i) => {
      const on = value.includes(o);
      return (
        <label key={o} className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm cursor-pointer transition-colors ${on ? "border-[#E60000] bg-[#FFF0F0]" : "border-[#E5E7EB] hover:border-gray-400"}`}>
          <Checkbox checked={on} onCheckedChange={(c) => onChange(c ? [...value, o] : value.filter((x) => x !== o))} data-testid={`${testId}-opt-${i}`} />{o}
        </label>
      );
    })}
  </div>
);

const Control = ({ f, value, onChange, testId }) => {
  const txt = (e) => onChange(e.target.value);
  const common = { value: value ?? "", onChange: txt, placeholder: f.placeholder || undefined, className: "h-11", "data-testid": testId };
  switch (f.type) {
    case "region": return <Pick value={value || REGIONS[0]} onChange={onChange} options={REGIONS} testId="profile-region-select" className="h-11" />;
    case "sex": return <Choice value={value} onChange={onChange} options={["Male", "Female"]} testId="profile-sex" />;
    case "select": return <Pick value={value || undefined} onChange={onChange} options={f.options} testId={testId} className="h-11" />;
    case "multi": return <Multi value={value || []} onChange={onChange} options={f.options} testId={testId} />;
    case "textarea": return <Textarea {...common} className="min-h-[96px]" />;
    case "date": return <Input {...common} type="date" max={f.key === "dob" ? new Date().toISOString().slice(0, 10) : undefined} />;
    case "number": return <Input {...common} type="number" inputMode="decimal" />;
    case "phone": return <Input {...common} inputMode="tel" placeholder={f.placeholder || "024 000 0000"} />;
    case "email": return <Input {...common} type="email" />;
    case "member_id": return <Input {...common} className="h-11 font-mono uppercase" />;
    default: return <Input {...common} />;
  }
};

export const FieldInput = ({ f, p, set, slots }) => {
  const label = `${f.label}${f.required ? " *" : ""}`;
  const custom = !f.builtin;
  const value = custom ? p.custom?.[f.key] : p[f.key];
  const onChange = (v) => set(custom ? { custom: { ...(p.custom || {}), [f.key]: v } } : { [f.key]: v });
  const wide = f.width === "full" ? "sm:col-span-2 lg:col-span-3" : "";
  const help = f.help && <p className="text-xs text-gray-500 mt-1">{f.help}</p>;
  if (f.type === "pin" || f.type === "account_email" || slots?.[f.key]) return <div className={wide}>{slots?.[f.key]?.(label)}{help}</div>;
  if (f.type === "title") return <Title f={f} value={value ?? ""} onChange={onChange} label={label} />;
  if (f.type === "checkbox") {
    return (
      <div className={`sm:col-span-2 lg:col-span-3 ${wide}`}>
        <label className="flex items-start gap-3 rounded-md border border-[#E5E7EB] p-3 text-sm cursor-pointer">
          <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)} className="mt-0.5" data-testid={fieldTestId(f)} />
          <span>{label}{help}</span>
        </label>
      </div>
    );
  }
  return <div className={wide}><AuthField label={label}><Control f={f} value={value} onChange={onChange} testId={fieldTestId(f)} /></AuthField>{help}</div>;
};
